import { scopeProjectRef } from "@afar-research/client-runtime/environment";
import type { PullRequestDetail } from "@afar-research/contracts";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import { usePreparePullRequestThreadAction } from "../state/sourceControlActions";
import {
  usePullRequestSurfaceStore,
  type PullRequestSurfaceDescriptor,
} from "../pullRequestSurfaceStore";
import { useProjects } from "../state/entities";
import { useEnvironmentConnectionState, useEnvironments } from "../state/environments";
import { PullRequestHandoffForm } from "./PullRequestHandoffForm";

export function PullRequestHandoff({
  descriptor,
  detail,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
}) {
  const projects = useProjects();
  const connection = useEnvironmentConnectionState(descriptor.environmentId);
  const session = usePullRequestSurfaceStore((state) => state.handoffs.get(descriptor.id));
  const { environments } = useEnvironments();
  const project = projects.find(
    (candidate) =>
      candidate.environmentId === descriptor.environmentId && candidate.id === descriptor.projectId,
  );
  const environment = environments.find(
    (candidate) => candidate.environmentId === descriptor.environmentId,
  );
  const prepareAction = usePreparePullRequestThreadAction({
    environmentId: descriptor.environmentId,
    cwd: project?.workspaceRoot ?? null,
  });
  const openNewThread = useNewThreadHandler();
  if (
    !project ||
    !environment ||
    !session ||
    connection.data?.generation === undefined ||
    environment.connection.phase !== "ready"
  )
    return null;
  if (environment.serverConfig?.environment.capabilities.pullRequestCheckout !== "checkout-v1")
    return (
      <p className="py-4 text-xs text-muted-foreground">
        Update this Environment to enable explicit checkout and agent handoffs.
      </p>
    );
  return (
    <PullRequestHandoffForm
      session={session}
      authorityToken={`${connection.data.generation}:${project.workspaceRoot}`}
      target={{ ...detail, environmentId: descriptor.environmentId }}
      cwd={project.workspaceRoot}
      environmentLabel={environment.label}
      prepare={async (input) => {
        const result = await prepareAction.run(input);
        if (result._tag === "Failure")
          throw new Error(
            "Checkout did not return a confirmed result. Inspect the target checkout before trying again.",
          );
        return result.value;
      }}
      openDraft={async (input) => {
        await openNewThread(scopeProjectRef(descriptor.environmentId, descriptor.projectId), {
          forceNew: true,
          draftIdentity: { draftId: input.draftId, threadId: input.threadId },
          branch: input.branch,
          worktreePath: input.worktreePath,
          envMode: input.worktreePath === null ? "local" : "worktree",
          initialPrompt: input.initialPrompt,
        });
      }}
    />
  );
}
